import type { ApiSchemas } from '@rental/contracts/client'
import { isIsoDate } from '../board'
import { parseEuroCents } from '../money'

type Request = ApiSchemas['InterventionRequestInput']
export type InterventionList = ApiSchemas['InterventionList']
export type MaintenanceKind = Request['kind']

/** Every maintenance query lives under this root, so a new intervention refreshes them all. */
export const MAINTENANCE_QUERY_KEY = ['maintenance'] as const

/** The filter bar: an empty string is "no filter". */
export interface MaintenanceFilters {
  readonly vehicleId: string
  readonly from: string
  readonly to: string
  readonly kind: MaintenanceKind | ''
}

export const NO_FILTERS: MaintenanceFilters = { vehicleId: '', from: '', to: '', kind: '' }

export const MAINTENANCE_KINDS: readonly MaintenanceKind[] = ['service', 'repair']

export function maintenanceKey(filters: MaintenanceFilters) {
  return [...MAINTENANCE_QUERY_KEY, filters] as const
}

/** The query string of the global list: only the filters that are set. */
export function listQuery(filters: MaintenanceFilters) {
  return {
    ...(filters.vehicleId ? { vehicleId: filters.vehicleId } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
  }
}

/** The header total is what the server computed over the filtered rows: never summed here. */
export function headerTotalCents(list: Pick<InterventionList, 'filteredTotalCents'>): number {
  return list.filteredTotalCents
}

/** A vehicle's history total is the server's all-time figure: never summed here. */
export function allTimeTotalCents(list: Pick<InterventionList, 'allTimeTotalCents'>): number {
  return list.allTimeTotalCents
}

/** One vehicle's history, under the shared root so a new intervention refreshes it too. */
export function vehicleHistoryKey(vehicleId: string) {
  return [...MAINTENANCE_QUERY_KEY, 'vehicle', vehicleId] as const
}

export interface InterventionDraft {
  readonly vehicleId: string
  readonly kind: MaintenanceKind
  readonly performedOn: string
  readonly description: string
  readonly cost: string
  readonly supplier: string
}

export function emptyDraft(today: string, vehicleId = ''): InterventionDraft {
  return {
    vehicleId,
    kind: 'service',
    performedOn: today,
    description: '',
    cost: '',
    supplier: '',
  }
}

export type DraftField = 'vehicleId' | 'performedOn' | 'description' | 'cost'

export type BodyOutcome =
  | { ok: true; body: Request }
  | { ok: false; invalid: readonly DraftField[] }

/** The cost is optional; when typed it is a non-negative amount, held as integer cents. */
function costOf(text: string): { ok: true; cents?: number } | { ok: false } {
  if (text.trim() === '') return { ok: true }
  const cents = parseEuroCents(text)
  return cents === null ? { ok: false } : { ok: true, cents }
}

/** The form's draft as the contract's body, or the fields that are not valid. */
export function interventionBody(draft: InterventionDraft): BodyOutcome {
  const invalid: DraftField[] = []
  if (draft.vehicleId === '') invalid.push('vehicleId')
  if (!isIsoDate(draft.performedOn)) invalid.push('performedOn')
  const description = draft.description.trim()
  if (description === '') invalid.push('description')
  const cost = costOf(draft.cost)
  if (!cost.ok) invalid.push('cost')
  if (invalid.length > 0 || !cost.ok) return { ok: false, invalid }
  const supplier = draft.supplier.trim()
  return {
    ok: true,
    body: {
      vehicleId: draft.vehicleId,
      kind: draft.kind,
      performedOn: draft.performedOn,
      description,
      ...(cost.cents !== undefined ? { costCents: cost.cents } : {}),
      ...(supplier ? { supplier } : {}),
    },
  }
}
